<template>
  <v-container>
    <div v-if="isLoading" class="loading-container">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </div>

    <v-row v-if="!isLoading">
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <TenantBreadcrumb
                v-if="tenant"
                v-bind:name="tenant.tenant_name"
                sub="HP2/OJ Branding Settings"
            ></TenantBreadcrumb>
            <v-spacer></v-spacer>
            <v-chip
                v-if="tab === 0 && hasDraftBranding"
                small
                color="warning"
                text-color="black"
            >
              Draft Saved
            </v-chip>
          </v-card-title>

          <v-tabs v-model="tab" background-color="transparent">
            <v-tab>Branding</v-tab>
            <v-tab>Email</v-tab>
          </v-tabs>

          <v-divider></v-divider>

          <v-tabs-items v-model="tab">
            <!-- Branding Tab -->
            <v-tab-item>
              <v-card-text>
                <div class="branding-page">
                  <section class="card">
                    <h2>Logo</h2>
                    <div class="logo-uploader">
                      <div class="data-preview logo-current-preview" v-if="currentBrandData.logoUrl">
                        <p>Current Logo:</p>
                        <img :src="currentBrandData.logoUrl" alt="Current Logo" />
                      </div>
                      <div class="file-input-field">
                        <label for="logo-upload" class="file-input-label">Choose New Logo</label>
                        <input id="logo-upload" type="file" @change="handleLogoUpload" accept="image/*" class="file-input-hidden" ref="logoInput" />
                        <span v-if="brandData.logoFileName" class="file-name">{{ brandData.logoFileName }}</span>
                        <span v-else class="file-name-placeholder">No file chosen</span>
                        <button v-if="brandData.logoFileName" @click="removeSelectedLogo" class="remove-logo-btn">
                          Remove
                        </button>
                      </div>
                      <div v-if="logoPreviewUrl" class="logo-preview">
                        <p>New Logo Preview:</p>
                        <img :src="logoPreviewUrl" alt="Logo Preview" />
                      </div>
                    </div>
                  </section>
                  <section class="card">
                    <h2>Branding settings</h2>
                    <p>Choose your brand colors and ensure they are accessible.</p>
                    <div class="color-inputs">
                      <div class="input-group">
                        <label for="primary-color">Primary Color <span class="required-text">*</span></label>
                        <div class="color-input-wrapper">
                          <input id="primary-color" type="color" v-model="brandData.colors.primary" />
                          <input type="text" v-model="brandData.colors.primary" placeholder="#RRGGBB" />
                        </div>
                      </div>
                      <div class="input-group">
                        <label for="secondary-color">Secondary Color <span class="required-text">*</span></label>
                        <div class="color-input-wrapper">
                          <input id="secondary-color" type="color" v-model="brandData.colors.secondary" />
                          <input type="text" v-model="brandData.colors.secondary" placeholder="#RRGGBB" />
                        </div>
                      </div>
                      <div class="input-group">
                        <label for="bg-color">Background Color <span class="required-text">*</span></label>
                        <div class="color-input-wrapper">
                          <input id="bg-color" type="color" v-model="brandData.colors.background" />
                          <input type="text" v-model="brandData.colors.background" placeholder="#RRGGBB" />
                        </div>
                      </div>
                    </div>
                    <div class="results-list">
                      <h3>Contrast Results</h3>
                      <ul>
                        <li>
                          <div class="pair-preview" :style="{ backgroundColor: brandData.colors.primary, color: '#FFFFFF' }">
                            Primary Color Buttons
                          </div>
                          <div class="scores">
                            <strong>{{ primaryButtonContrast.ratio.toFixed(2) }}:1</strong>
                            <span :class="{ pass: primaryButtonContrast.aa, fail: !primaryButtonContrast.aa }">AA</span>
                            <span :class="{ pass: primaryButtonContrast.aaa, fail: !primaryButtonContrast.aaa }">AAA</span>
                          </div>
                        </li>
                        <li>
                          <div class="pair-preview" :style="{ backgroundColor: brandData.colors.secondary, color: '#FFFFFF' }">
                            Secondary Color Buttons
                          </div>
                          <div class="scores">
                            <strong>{{ secondaryButtonContrast.ratio.toFixed(2) }}:1</strong>
                            <span :class="{ pass: secondaryButtonContrast.aa, fail: !secondaryButtonContrast.aa }">AA</span>
                            <span :class="{ pass: secondaryButtonContrast.aaa, fail: !secondaryButtonContrast.aaa }">AAA</span>
                          </div>
                        </li>
                        <li>
                          <div class="pair-preview" :style="{ backgroundColor: '#FFFFFF', color: brandData.colors.primary }">
                            Primary on White Background
                          </div>
                          <div class="scores">
                            <strong>{{ primaryOnWhiteContrast.ratio.toFixed(2) }}:1</strong>
                            <span :class="{ pass: primaryOnWhiteContrast.aa, fail: !primaryOnWhiteContrast.aa }">AA</span>
                            <span :class="{ pass: primaryOnWhiteContrast.aaa, fail: !primaryOnWhiteContrast.aaa }">AAA</span>
                          </div>
                        </li>
                        <li>
                          <div class="pair-preview" :style="{ backgroundColor: '#FFFFFF', color: brandData.colors.secondary }">
                            Secondary on White Background
                          </div>
                          <div class="scores">
                            <strong>{{ secondaryOnWhiteContrast.ratio.toFixed(2) }}:1</strong>
                            <span :class="{ pass: secondaryOnWhiteContrast.aa, fail: !secondaryOnWhiteContrast.aa }">AA</span>
                            <span :class="{ pass: secondaryOnWhiteContrast.aaa, fail: !secondaryOnWhiteContrast.aaa }">AAA</span>
                          </div>
                        </li>
                      </ul>
                    </div>
                    <div class="data-preview color-preview-wrapper" v-if="currentBrandData.colors && currentBrandData.colors.primary">
                      <span>Current Colors:</span>
                      <div class="color-swatches">
                        <div class="swatch-container">
                          <span class="swatch" :style="{ backgroundColor: currentBrandData.colors.primary }"></span>
                          <small>Primary</small>
                        </div>
                        <div class="swatch-container">
                          <span class="swatch" :style="{ backgroundColor: currentBrandData.colors.secondary }"></span>
                          <small>Secondary</small>
                        </div>
                        <div class="swatch-container">
                          <span class="swatch" :style="{ backgroundColor: currentBrandData.colors.background }"></span>
                          <small>Background</small>
                        </div>
                      </div>
                    </div>
                  </section>
                  <section class="card">
                    <h2>Website Content<br>
                      <span>Leave blank to use defaults</span>
                    </h2>
                    <v-tabs v-model="contentTab" background-color="transparent" color="primary" grow>
                      <v-tab>English</v-tab>
                      <v-tab>Welsh</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="contentTab">
                      <!-- English Tab -->
                      <v-tab-item>
                        <div class="content-inputs tab-content">
                          <div class="input-field">
                            <label for="heading-en">Onlinejoining heading (English)</label>
                            <input id="heading-en" type="text" v-model="brandData.content.en.heading" placeholder="Online booking" />
                            <div v-if="currentBrandData.content.en.heading" class="data-preview">
                              Current: {{ currentBrandData.content.en.heading }}
                            </div>
                          </div>
                          <div class="input-field">
                            <label for="subheading-en">Onlinejoining Sub-Heading (English)</label>
                            <input id="subheading-en" type="text" v-model="brandData.content.en.subHeading" placeholder="Find a centre near you..." />
                            <div v-if="currentBrandData.content.en.subHeading" class="data-preview">
                              Current: {{ currentBrandData.content.en.subHeading }}
                            </div>
                          </div>
                          <div class="input-field">
                            <label for="terms-en">Terms and Conditions External URL (English)</label>
                            <input id="terms-en" type="text" v-model="brandData.content.en.terms" placeholder="https://example.com/terms" />
                            <div v-if="currentBrandData.content.en.terms" class="data-preview">
                              Current: <a :href="currentBrandData.content.en.terms" target="_blank" rel="noopener noreferrer">{{ currentBrandData.content.en.terms }}</a>
                            </div>
                          </div>
                          <div class="input-field">
                            <label for="privacy-en">Privacy Policy External URL (English)</label>
                            <input id="privacy-en" type="text" v-model="brandData.content.en.privacy" placeholder="https://example.com/privacy" />
                            <div v-if="currentBrandData.content.en.privacy" class="data-preview">
                              Current: <a :href="currentBrandData.content.en.privacy" target="_blank" rel="noopener noreferrer">{{ currentBrandData.content.en.privacy }}</a>
                            </div>
                          </div>
                        </div>
                      </v-tab-item>

                      <!-- Welsh Tab -->
                      <v-tab-item>
                        <div class="content-inputs tab-content">
                          <div class="input-field">
                            <label for="heading-cy">Onlinejoining heading (Welsh)</label>
                            <input id="heading-cy" type="text" v-model="brandData.content.cy.heading" placeholder="Archebu ar-lein" />
                            <div v-if="currentBrandData.content.cy.heading" class="data-preview">
                              Current: {{ currentBrandData.content.cy.heading }}
                            </div>
                          </div>
                          <div class="input-field">
                            <label for="subheading-cy">Onlinejoining Sub-Heading (Welsh)</label>
                            <input id="subheading-cy" type="text" v-model="brandData.content.cy.subHeading" placeholder="Dewch o hyd i ganolfan yn eich ardal chi..." />
                            <div v-if="currentBrandData.content.cy.subHeading" class="data-preview">
                              Current: {{ currentBrandData.content.cy.subHeading }}
                            </div>
                          </div>
                          <div class="input-field">
                            <label for="terms-cy">Terms and Conditions External URL (Welsh)</label>
                            <input id="terms-cy" type="text" v-model="brandData.content.cy.terms" placeholder="https://example.com/telerau" />
                            <div v-if="currentBrandData.content.cy.terms" class="data-preview">
                              Current: <a :href="currentBrandData.content.cy.terms" target="_blank" rel="noopener noreferrer">{{ currentBrandData.content.cy.terms }}</a>
                            </div>
                          </div>
                          <div class="input-field">
                            <label for="privacy-cy">Privacy Policy External URL (Welsh)</label>
                            <input id="privacy-cy" type="text" v-model="brandData.content.cy.privacy" placeholder="https://example.com/preifatrwydd" />
                            <div v-if="currentBrandData.content.cy.privacy" class="data-preview">
                              Current: <a :href="currentBrandData.content.cy.privacy" target="_blank" rel="noopener noreferrer">{{ currentBrandData.content.cy.privacy }}</a>
                            </div>
                          </div>
                        </div>
                      </v-tab-item>
                    </v-tabs-items>
                  </section>
                </div>
              </v-card-text>
              <v-card-actions class="pa-5">
                <v-row no-gutters justify="end" align="center" style="width: 100%;">
                  <v-col cols="12" md="4" class="py-0">
                    <v-select
                        :items="branches"
                        v-model="release.branch"
                        label="Select a branch for generation"
                    />
                  </v-col>
                  <v-col cols="auto" class="pl-md-3">
                    <v-btn
                        color="secondary"
                        :loading="isSaving"
                        @click="saveBranding"
                    >
                      Save Draft
                    </v-btn>
                  </v-col>
                  <v-col cols="auto" class="pl-md-3">
                    <v-btn
                        outlined
                        color="white"
                        class="preview-launch-btn"
                        @click="showBrandingPreview = true"
                    >
                      Preview
                    </v-btn>
                  </v-col>
                  <v-col cols="auto" class="pl-md-3">
                    <v-btn
                        color="primary"
                        :loading="isGenerating"
                        @click="openConfirmationDialog"
                        :disabled="!release.branch || isGenerating"
                    >
                      Generate
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-actions>
            </v-tab-item>

            <!-- Email Tab -->
            <v-tab-item>
              <v-card-text>
                <div class="email-page">
                  <v-row>
                    <v-col cols="12" md="5">
                      <section class="card">
                        <h2>Email Header</h2>
                        <div class="input-group" style="width: 100%; margin-bottom: 25px;">
                          <label for="email-header-bg">Header Background</label>
                          <div class="color-input-wrapper">
                            <input id="email-header-bg" type="color" v-model="brandData.email.header.backgroundColor" />
                            <input type="text" v-model="brandData.email.header.backgroundColor" placeholder="#RRGGBB" />
                          </div>
                        </div>
                        <div class="logo-uploader">
                          <div class="data-preview logo-current-preview" v-if="currentBrandData.email.header.logoUrl">
                            <p>Current Logo:</p>
                            <img :src="currentBrandData.email.header.logoUrl" alt="Current Email Logo" />
                          </div>
                          <div class="file-input-field">
                            <label for="email-logo-upload" class="file-input-label">Choose New Logo</label>
                            <input id="email-logo-upload" type="file" @change="handleEmailLogoUpload" accept="image/*" class="file-input-hidden" ref="emailLogoInput" />
                            <span v-if="brandData.emailLogoFileName" class="file-name">{{ brandData.emailLogoFileName }}</span>
                            <span v-else class="file-name-placeholder">No file chosen</span>
                            <button v-if="brandData.emailLogoFileName" @click="removeSelectedEmailLogo" class="remove-logo-btn">
                              Remove
                            </button>
                          </div>
                          <div v-if="emailLogoPreviewUrl" class="logo-preview">
                            <p>New Logo Preview:</p>
                            <img :src="emailLogoPreviewUrl" alt="Email Logo Preview" />
                          </div>
                        </div>
                        <v-select
                            :items="[{text: 'Left', value: 'flex-start'}, {text: 'Center', value: 'center'}, {text: 'Right', value: 'flex-end'}]"
                            v-model="brandData.email.header.logoPosition"
                            label="Logo Position"
                            class="mt-6"
                        ></v-select>
                      </section>

                      <section class="card">
                        <h2>Email content</h2>
                        <div class="color-inputs">
                          <div class="input-group">
                            <label for="email-container-bg">Container BG</label>
                            <div class="color-input-wrapper">
                              <input id="email-container-bg" type="color" v-model="brandData.email.containerBackgroundColor" />
                              <input type="text" v-model="brandData.email.containerBackgroundColor" placeholder="#RRGGBB" />
                            </div>
                          </div>
                          <div class="input-group">
                            <label for="email-content-bg">Content Background</label>
                            <div class="color-input-wrapper">
                              <input id="email-content-bg" type="color" v-model="brandData.email.contentBackgroundColor" />
                              <input type="text" v-model="brandData.email.contentBackgroundColor" placeholder="#RRGGBB" />
                            </div>
                          </div>
                          <div class="input-group">
                            <label for="email-content-text">Content Text</label>
                            <div class="color-input-wrapper">
                              <input id="email-content-text" type="color" v-model="brandData.email.contentTextColor" />
                              <input type="text" v-model="brandData.email.contentTextColor" placeholder="#RRGGBB" />
                            </div>
                          </div>
                        </div>
                        <v-row class="mt-4">
                          <v-col cols="12" md="6">
                            <v-text-field label="Heading Text" v-model="brandData.email.header.headingText" outlined dense></v-text-field>
                          </v-col>
                          <v-col cols="12" md="6">
                            <div class="input-group" style="width: 100%;">
                              <label for="email-heading-text-color">Heading Text Color</label>
                              <div class="color-input-wrapper">
                                <input id="email-heading-text-color" type="color" v-model="brandData.email.header.headingTextColor" />
                                <input type="text" v-model="brandData.email.header.headingTextColor" placeholder="#RRGGBB" />
                              </div>
                            </div>
                          </v-col>
                        </v-row>
                        <v-row class="mt-4">
                          <v-col cols="12" md="6">
                            <v-text-field label="Sub-Heading Text" v-model="brandData.email.header.subheadingText" outlined dense></v-text-field>
                          </v-col>
                          <v-col cols="12" md="6">
                            <div class="input-group" style="width: 100%;">
                              <label for="email-subheading-text-color">Sub-Heading Text Color</label>
                              <div class="color-input-wrapper">
                                <input id="email-subheading-text-color" type="color" v-model="brandData.email.header.subheadingTextColor" />
                                <input type="text" v-model="brandData.email.header.subheadingTextColor" placeholder="#RRGGBB" />
                              </div>
                            </div>
                          </v-col>
                        </v-row>
                      </section>
                      <section class="card">
                        <h2>Email Footer</h2>
                        <div class="color-inputs" style="margin-bottom: 25px;">
                          <div class="input-group">
                            <label for="email-footer-bg">Footer BG</label>
                            <div class="color-input-wrapper">
                              <input id="email-footer-bg" type="color" v-model="brandData.email.footer.backgroundColor" />
                              <input type="text" v-model="brandData.email.footer.backgroundColor" placeholder="#RRGGBB" />
                            </div>
                          </div>
                          <div class="input-group">
                            <label for="email-footer-text">Footer Text</label>
                            <div class="color-input-wrapper">
                              <input id="email-footer-text" type="color" v-model="brandData.email.footer.textColor" />
                              <input type="text" v-model="brandData.email.footer.textColor" placeholder="#RRGGBB" />
                            </div>
                          </div>
                        </div>
                        <v-textarea
                            label="Footer Content"
                            v-model="brandData.email.footer.content"
                            outlined
                            dense
                            rows="3"
                            placeholder="Enter footer text here. HTML tags like <a> are allowed."
                        ></v-textarea>
                      </section>
                    </v-col>

                    <v-col cols="12" md="7">
                      <section class="card">
                        <h2>Live Preview</h2>
                        <div class="email-preview-container">
                          <div v-html="generatedEmailHtml"></div>
                        </div>
                      </section>

                      <section class="card mt-6">
                        <h2>Email header html</h2>
                        <div class="copyable-field-container">
                          <v-textarea
                              class="code-textarea"
                              :value="generatedEmailHeaderHtml"
                              outlined
                              rows="6"
                              hide-details
                          ></v-textarea>
                          <v-btn icon class="copy-button" @click="copyToClipboard(generatedEmailHeaderHtml, 'Email header HTML')">
                            <v-icon>mdi-content-copy</v-icon>
                          </v-btn>
                        </div>
                      </section>

                      <section class="card mt-6">
                        <h2>Email footer html</h2>
                        <div class="copyable-field-container">
                          <v-textarea
                              class="code-textarea"
                              :value="generatedEmailFooterHtml"
                              outlined
                              rows="6"
                              hide-details
                          ></v-textarea>
                          <v-btn icon class="copy-button" @click="copyToClipboard(generatedEmailFooterHtml, 'Email footer HTML')">
                            <v-icon>mdi-content-copy</v-icon>
                          </v-btn>
                        </div>
                      </section>
                    </v-col>
                  </v-row>
                </div>
              </v-card-text>
              <v-card-actions class="pa-5">
                <v-row no-gutters justify="end" align="center" style="width: 100%;">
                  <v-col cols="auto">
                    <v-btn
                        color="primary"
                        :loading="isSavingEmail"
                        @click="saveEmailSettings"
                    >
                      Save Email Settings
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-actions>
            </v-tab-item>
          </v-tabs-items>

        </v-card>
      </v-col>
    </v-row>
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
    <v-dialog v-model="showConfirmationDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Confirm Action</v-card-title>
        <v-card-text>
          Generate branding CSS for this tenant using the most recently saved draft branding settings?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showConfirmationDialog = false">Cancel</v-btn>
          <v-btn color="primary" text @click="generateBranding">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
        v-model="showBrandingPreview"
        fullscreen
        hide-overlay
        transition="dialog-bottom-transition"
    >
      <v-card class="preview-dialog-card">
        <v-toolbar flat dense class="preview-dialog-toolbar">
          <v-chip small outlined color="white">
            {{ tenant ? tenant.tenant_name : 'Tenant' }}
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn
              icon
              aria-label="Close preview"
              title="Close preview"
              @click="showBrandingPreview = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="preview-dialog-content">
          <TenantHomeportal2BrandingPreview
              :key="previewRenderKey"
              :brand-data="brandData"
              :logo-preview-url="logoPreviewUrl"
              :tenant-name="tenant ? tenant.tenant_name : 'Tenant'"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import TenantBreadcrumb from "@/components/TenantBreadcrumb";
import TenantHomeportal2BrandingPreview from "@/components/TenantHomeportal2BrandingPreview";
import tinycolor from 'tinycolor2';
import DOMPurify from 'dompurify';
import { http, apiUrl } from '@/services/http';

export default {
  name: 'TenantHomeportal2Branding',
  components: { TenantBreadcrumb, TenantHomeportal2BrandingPreview },
  data: () => ({
    tab: null,
    release: { app: 'coursepro', branch: null },
    branches: [],
    isLoading: true,
    isSaving: false,
    isGenerating: false,
    isSavingEmail: false,
    showConfirmationDialog: false,
    showBrandingPreview: false,
    previewRenderKey: 0,
    tenant: null,
    contentTab: null,
    brandData: {
      colors: { primary: '#4299e1', secondary: '#f6ad55', background: '#e8e8e8' },
      logoUrl: null,
      logoFileName: '',
      emailLogoFileName: '',
      content: {
        en: { heading: '', subHeading: '', terms: '', privacy: '' },
        cy: { heading: '', subHeading: '', terms: '', privacy: '' }
      },
      email: {
        containerBackgroundColor: '#DDDDDD',
        contentBackgroundColor: '#FFFFFF',
        contentTextColor: '#444444',
        header: {
          logoUrl: null,
          logoPosition: 'left',
          backgroundColor: '#FFFFFF',
          headingText: '',
          subheadingText: '',
          headingTextColor: '#000',
          subheadingTextColor: '#000',
        },
        footer: {
          backgroundColor: '#4299e1',
          textColor: '#FFFFFF',
          content: 'Email us <a style="color:#FFFFFF;" href="mailto:hello@fitronics.com">hello@fitronics.com</a></a><br/>Contact us: <a style="color:#FFFFFF;" href="https://www.fitronics.com" target="_blank">https://www.fitronics.com/</a>'
        }
      }
    },
    currentBrandData: {
      colors: {},
      logoUrl: null,
      content: { en: {}, cy: {} },
      email: { header: {}, footer: {} }
    },
    logoFile: null,
    logoPreviewUrl: null,
    emailLogoFile: null,
    emailLogoPreviewUrl: null,
    hasDraftBranding: false,
    snackbar: { show: false, text: '', color: 'success' }
  }),
  computed: {
    generatedEmailHeaderHtml() {
      const { email } = this.brandData;
      const tenantName = this.tenant?.tenant_name;
      const logoSource = tenantName ? `https://homeportal2.s3.eu-west-1.amazonaws.com/html/${tenantName}/images/${tenantName}-logo.png` : '';
      const logoHtml = logoSource ? `<img src="${logoSource}" style="max-width: 150px; background: transparent;"/>` : '';
      const heading = email.header.headingText ? `<span style="font-weight: bold;display: block;font-size: 18px;color: ${email.header.headingTextColor};">${email.header.headingText}</span>` : '';
      const subheading = email.header.subheadingText ? `<span style="font-weight: bold;display: block;color: ${email.header.subheadingTextColor};">${email.header.subheadingText}</span>` : '';
      const innerHeaderContent = heading || subheading ? `${heading}${subheading}<br>` : '';

      return `<div style="font-family: Arial, sans-serif; background: ${email.containerBackgroundColor};">
  <div style="width: 100%; max-width: 600px; margin: 0 auto; display: block; border-left: 1px solid #ccc; border-right: 1px solid #ccc;">
    <div style="background: ${email.header.backgroundColor}; border-bottom: 1px solid #ccc; padding: 20px; display: flex; justify-content: ${email.header.logoPosition}; font-size: 18px; font-weight: bold;">
      ${logoHtml}
    </div>
    <div style="padding: 20px; color: ${email.contentTextColor}; font-size: 15px; background: ${email.contentBackgroundColor};">
      ${innerHeaderContent}`;
    },
    generatedEmailFooterHtml() {
      const { email } = this.brandData;
      if (!email.footer.content) {
        return `</div>
  </div>
</div>`;
      }
      return `</div>
        <div style="background: ${email.footer.backgroundColor}; border-top: 1px solid #007D8B; padding: 20px; color: ${email.footer.textColor}; font-size: 12px;">
          ${email.footer.content}
        </div>
      </div>
    </div>
  `;
    },
    generatedEmailHtml() {
      const { email } = this.brandData;
      const logoSource = this.emailLogoPreviewUrl || email.header.logoUrl;
      const logoHtml = logoSource ? `<img src="${logoSource}" style="max-width: 150px; background: transparent;"/>` : '';
      const heading = email.header.headingText ? `<span style="font-weight: bold;display: block;font-size: 18px;color: ${email.header.headingTextColor};">${email.header.headingText}</span>` : '';
      const subheading = email.header.subheadingText ? `<span style="font-weight: bold;display: block;color: ${email.header.subheadingTextColor};">${email.header.subheadingText}</span>` : '';
      const headerContent = heading || subheading ? `${heading}${subheading}<br>` : '';

      const header = `
        <div style="font-family: Arial, sans-serif; background: ${email.containerBackgroundColor};">
          <div style="width: 100%; max-width: 600px; margin: 0 auto; display: block; border-left: 1px solid #ccc; border-right: 1px solid #ccc;">
            <div style="background: ${email.header.backgroundColor}; border-bottom: 1px solid #ccc; padding: 20px; display: flex; justify-content: ${email.header.logoPosition}; font-size: 18px; font-weight: bold;">
              ${logoHtml}
            </div>
            <div style="padding: 20px; color: ${email.contentTextColor}; font-size: 15px; background: ${email.contentBackgroundColor};">
              ${headerContent}
              <p>This is where the main body content of the email will be dynamically inserted by the system. This preview shows the header and footer you are creating.</p>
              <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer nec odio. Praesent libero. Sed cursus ante dapibus diam.</p>
            `;

      const footer = email.footer.content ? `
            </div>
            <div style="background: ${email.footer.backgroundColor}; border-top: 1px solid #007D8B; padding: 20px; color: ${email.footer.textColor}; font-size: 12px;">
              ${email.footer.content}
            </div>
          </div>
        </div>
      ` : `
        </div>
      </div>
    </div>
    `;

      return DOMPurify.sanitize(header + footer, {
        ALLOWED_TAGS: ['div', 'span', 'p', 'br', 'img', 'a', 'table', 'tr', 'td', 'th', 'thead', 'tbody', 'strong', 'em', 'b', 'i', 'u', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
        ALLOWED_ATTR: ['style', 'src', 'alt', 'href', 'target', 'class', 'width', 'height'],
        ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
      });
    },
    primaryButtonContrast() {
      if (!this.brandData.colors.primary) return { ratio: 0, aa: false, aaa: false };
      return this.calculateContrast(this.brandData.colors.primary, '#FFFFFF');
    },
    secondaryButtonContrast() {
      if (!this.brandData.colors.secondary) return { ratio: 0, aa: false, aaa: false };
      return this.calculateContrast(this.brandData.colors.secondary, '#FFFFFF');
    },
    primaryOnWhiteContrast() {
      if (!this.brandData.colors.primary) return { ratio: 0, aa: false, aaa: false };
      return this.calculateContrast('#FFFFFF', this.brandData.colors.primary);
    },
    secondaryOnWhiteContrast() {
      if (!this.brandData.colors.secondary) return { ratio: 0, aa: false, aaa: false };
      return this.calculateContrast('#FFFFFF', this.brandData.colors.secondary);
    },
    hasUnsavedBrandingChanges() {
      if (this.logoFile || this.logoPreviewUrl) {
        return true;
      }

      return JSON.stringify(this.getComparableBrandingState(this.brandData))
        !== JSON.stringify(this.getComparableBrandingState(this.currentBrandData));
    }
  },
  async created() {
    try {
      await Promise.all([ this.getTenant(), this.getBrandingData(), this.getBranches() ]);
    } catch (error) {
      this.showSnackbar('Failed to load initial page data.', 'error');
      console.error("Error during created hook:", error);
    } finally {
      this.isLoading = false;
    }
  },
  watch: {
    showBrandingPreview(isOpen) {
      if (isOpen) {
        this.previewRenderKey += 1;
      }
    }
  },
  methods: {
    async saveBranding(showSuccess = true) {
      this.isSaving = true;
      const formData = new FormData();
      if (this.logoFile) formData.append('logo', this.logoFile);

      formData.append('brandingData', JSON.stringify({
        ...this.brandData,
      }));
      formData.append('contentData', JSON.stringify(this.brandData.content));

      try {
        const response = await http.post(`${apiUrl}/tenant/${this.$route.params.id}/homeportal2/branding`, formData);
        if (response.data.logoUrl) this.brandData.logoUrl = response.data.logoUrl;
        this.currentBrandData.logoUrl = this.brandData.logoUrl;
        this.removeSelectedLogo();
        this.currentBrandData = JSON.parse(JSON.stringify(this.brandData));
        if (showSuccess) {
          this.showSnackbar('Draft branding and content settings saved successfully.', 'success');
        }
        this.hasDraftBranding = true;
        return true;
      } catch (error) {
        console.error("Error saving branding settings:", error);
        this.showSnackbar('Failed to save branding settings. Please try again.', 'error');
        return false;
      } finally {
        this.isSaving = false;
      }
    },
    async generateBranding() {
      this.showConfirmationDialog = false;
      if (!this.release.branch) {
        this.showSnackbar('Please select a branch to generate branding CSS.', 'error');
        return;
      }

      if (this.hasUnsavedBrandingChanges) {
        this.showSnackbar('Save Draft before generating so the published branding matches the saved draft.', 'error');
        return;
      }

      if (!this.hasDraftBranding) {
        this.showSnackbar('Save Draft before generating branding CSS.', 'error');
        return;
      }

      this.isGenerating = true;

      try {
        await http.post(`${apiUrl}/tenant/${this.$route.params.id}/homeportal2/branding/generate`, {
          app: this.release.app,
          branch: this.release.branch,
        });
        this.hasDraftBranding = false;
        try {
          await this.getBrandingData();
        } catch (refreshError) {
          console.warn('Failed to refresh branding after generate:', refreshError);
        }
        this.showSnackbar('Branding CSS generated successfully.', 'success');
      } catch (error) {
        console.error("Error generating branding CSS:", error);
        this.showSnackbar('Failed to generate branding CSS. Please try again.', 'error');
      } finally {
        this.isGenerating = false;
      }
    },
    getComparableBrandingState(source) {
      const state = source || {};

      return {
        colors: state.colors || {},
        logoUrl: state.logoUrl || null,
        content: state.content || { en: {}, cy: {} }
      };
    },
    async saveEmailSettings() {
      this.isSavingEmail = true;
      const formData = new FormData();
      if (this.emailLogoFile) formData.append('email_logo', this.emailLogoFile);

      formData.append('emailData', JSON.stringify(this.brandData.email));

      try {
        const response = await http.post(`${apiUrl}/tenant/${this.$route.params.id}/homeportal2/email`, formData);
        if (response.data.emailLogoUrl) {
          this.brandData.email.header.logoUrl = response.data.emailLogoUrl;
          this.currentBrandData.email.header.logoUrl = response.data.emailLogoUrl;
        }
        this.removeSelectedEmailLogo();
        this.showSnackbar('Email settings saved successfully!', 'success');
      } catch (error) {
        console.error("Error saving email settings:", error);
        this.showSnackbar('Failed to save email settings. Please try again.', 'error');
      } finally {
        this.isSavingEmail = false;
      }
    },
    async getBrandingData() {
      try {
        const response = await http.get(`${apiUrl}/tenant/${this.$route.params.id}/homeportal2/branding`);
        const branding = response.data.data || {};
        this.hasDraftBranding = Boolean(branding.has_draft);

        // Branding colors and logo
        this.brandData.colors = { ...this.brandData.colors, ...branding.colors };
        this.brandData.logoUrl = branding.logo_url || null;

        // Website Content
        this.brandData.content.en.heading = branding.en?.find_a_center_title || '';
        this.brandData.content.en.subHeading = branding.en?.find_a_center_text || '';
        this.brandData.content.en.terms = branding.en?.terms_link_external || '';
        this.brandData.content.en.privacy = branding.en?.privacy_policy_link_external || '';
        this.brandData.content.cy.heading = branding.cy?.find_a_center_title || '';
        this.brandData.content.cy.subHeading = branding.cy?.find_a_center_text || '';
        this.brandData.content.cy.terms = branding.cy?.terms_link_external || '';
        this.brandData.content.cy.privacy = branding.cy?.privacy_policy_link_external || '';

        // Email settings
        if (branding.email) {
          this.brandData.email.containerBackgroundColor = branding.email.containerBackgroundColor || this.brandData.email.containerBackgroundColor;
          this.brandData.email.contentBackgroundColor = branding.email.contentBackgroundColor || this.brandData.email.contentBackgroundColor;
          this.brandData.email.contentTextColor = branding.email.contentTextColor || this.brandData.email.contentTextColor;
          this.brandData.email.header = {
            ...this.brandData.email.header,
            ...branding.email.header,
            logoUrl: branding.email.header?.logoUrl || null,
            headingText: branding.email?.header?.headingText || '',
            subheadingText: branding.email?.header?.subheadingText || '',
            headingTextColor: branding.email?.header?.headingTextColor || '#000',
            subheadingTextColor: branding.email?.header?.subheadingTextColor || '#000',
          };
          this.brandData.email.footer = { ...this.brandData.email.footer, ...branding.email.footer };
        }
        this.currentBrandData = JSON.parse(JSON.stringify(this.brandData));
      } catch (error) {
        if (error.response && error.response.status === 404) {
          console.log("No existing branding file found. Using default settings.");
        } else {
          this.showSnackbar('Could not load existing branding settings.', 'error');
          console.error("Error fetching branding data:", error);
        }
      }
    },
    handleEmailLogoUpload(event) {
      const file = event.target.files[0];
      if (!file) {
        this.emailLogoFile = null;
        this.emailLogoPreviewUrl = null;
        this.brandData.emailLogoFileName = '';
        return;
      }
      this.emailLogoFile = file;
      this.brandData.emailLogoFileName = file.name;
      const reader = new FileReader();
      reader.onload = (e) => this.emailLogoPreviewUrl = e.target.result;
      reader.readAsDataURL(file);
    },
    removeSelectedEmailLogo() {
      this.emailLogoFile = null;
      this.emailLogoPreviewUrl = null;
      this.brandData.emailLogoFileName = '';
      if (this.$refs.emailLogoInput) this.$refs.emailLogoInput.value = null;
    },
    openConfirmationDialog() { this.showConfirmationDialog = true; },
    async getBranches() {
      const params = { app: this.release.app };
      const response = await http.get(`${apiUrl}/repos/branches`, { params });
      this.branches = response.data;
    },
    async getTenant() {
      const response = await this.$http.get(process.env.VUE_APP_CONTROL_API_URL + "tenant/" + this.$route.params.id);
      this.tenant = response.data.tenant;
    },
    handleLogoUpload(event) {
      const file = event.target.files[0];
      if (!file) { this.removeSelectedLogo(); return; }
      this.logoFile = file;
      this.brandData.logoFileName = file.name;
      const reader = new FileReader();
      reader.onload = (e) => this.logoPreviewUrl = e.target.result;
      reader.readAsDataURL(file);
    },
    removeSelectedLogo() {
      this.logoFile = null;
      this.logoPreviewUrl = null;
      this.brandData.logoFileName = '';
      if(this.$refs.logoInput) this.$refs.logoInput.value = null;
    },
    calculateContrast(color1, color2) {
      const ratio = tinycolor.readability(color1, color2);
      return {
        ratio: ratio,
        aa: tinycolor.isReadable(color1, color2, {level: "AA", size: "small"}),
        aaa: tinycolor.isReadable(color1, color2, {level: "AAA", size: "small"})
      };
    },
    async copyToClipboard(text, fieldName) {
      try {
        await navigator.clipboard.writeText(text);
        this.showSnackbar(`${fieldName} copied to clipboard!`, 'success');
      } catch (err) {
        console.error('Failed to copy text:', err);
        this.showSnackbar(`Failed to copy ${fieldName}.`, 'error');
      }
    },
    showSnackbar(text, color) {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    }
  },
}
</script>

<style>
.email-preview-container {
  background-color: #3f3f3f;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #555;
  min-height: 400px;
  overflow-x: auto;
}
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
}
.branding-page, .email-page {
  padding-top: 20px;
  color: #e2e8f0;
}
.card {
  background-color: #272727;
  border: 1px solid #3f3f3f;
  border-radius: 8px;
  padding: 25px;
  margin-bottom: 30px;
}
.card h2 {
  margin-top: 0;
  border-bottom: 1px solid #3f3f3f;
  padding-bottom: 15px;
  margin-bottom: 20px;
}
.card h2 span {
  font-size: 14px;
  font-weight: normal;
}
.logo-uploader {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.file-input-field {
  display: flex;
  align-items: center;
  gap: 15px;
}
.file-input-hidden { display: none; }
.file-input-label {
  background-color: #4299e1;
  color: #1a202c;
  padding: 10px 15px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.2s ease;
  white-space: nowrap;
}
.file-input-label:hover { background-color: #3182ce; }
.file-name, .file-name-placeholder {
  background-color: #1f1f1f;
  padding: 8px 12px;
  border: 1px solid #3f3f3f;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.9rem;
  flex-grow: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.file-name-placeholder { color: #718096; }
.logo-preview, .logo-current-preview {
  margin-top: 15px;
  padding: 10px;
  border: 2px dashed #3f3f3f;
  border-radius: 5px;
  background-color: #1f1f1f;
  text-align: center;
}
.logo-preview img, .logo-current-preview img {
  max-width: 200px;
  max-height: 150px;
  margin-top: 10px;
}
.logo-current-preview p { margin: 0; font-weight: bold; color: #a0aec0; }
.content-inputs { display: flex; flex-direction: column; gap: 20px; }
.input-field { display: flex; flex-direction: column; }
.input-field label { font-weight: bold; margin-bottom: 8px; }
.input-field input[type="text"] {
  background-color: #1f1f1f;
  color: #e2e8f0;
  border: 1px solid #3f3f3f;
  border-radius: 4px;
  padding: 10px;
  font-size: 1rem;
  font-family: sans-serif;
}
/* autoprefixer: off */
.input-field input[type="text"]::placeholder { color: #718096; }
/* autoprefixer: on */
.data-preview { margin-top: 8px; font-size: 0.85rem; color: #a0aec0; padding: 5px 0; }
.data-preview a { color: #63b3ed; text-decoration: none; }
.data-preview a:hover { text-decoration: underline; }
.color-preview-wrapper {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #3f3f3f;
}
.color-swatches { display: flex; gap: 10px; }
.swatch-container { display: flex; flex-direction: column; align-items: center; gap: 5px; }
.swatch { width: 30px; height: 30px; border-radius: 50%; border: 2px solid #a0aec0; }
.color-inputs {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 20px;
}
.input-group { display: flex; flex-direction: column; align-items: center; gap: 8px; }
.input-group label { font-weight: bold; font-size: 1rem; }
.color-input-wrapper { display: flex; align-items: center; gap: 10px; }
.input-group input[type="color"] {
  width: 50px;
  height: 50px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  background-color: transparent;
}
.input-group input[type="color"]::-webkit-color-swatch-wrapper { padding: 0; }
.input-group input[type="color"]::-webkit-color-swatch { border: 1px solid #718096; border-radius: 8px; }
.input-group input[type="text"] {
  width: 90px;
  padding: 8px;
  border: 1px solid #3f3f3f;
  border-radius: 4px;
  font-family: monospace;
  font-size: 1rem;
  text-align: center;
  background-color: #1f1f1f;
  color: #e2e8f0;
}
.results-list { margin-top: 20px; }
.results-list h3 { text-align: center; margin-bottom: 15px; }
.results-list ul { list-style: none; padding: 0; max-width: 500px; margin: 0 auto; }
.results-list li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 10px;
  background-color: #3f3f3f;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
.pair-preview { padding: 10px 15px; border-radius: 5px; font-weight: bold; }
.scores { display: flex; align-items: center; gap: 15px; }
.scores strong { font-size: 1.1em; min-width: 50px; text-align: right; }
.scores span { padding: 4px 8px; border-radius: 4px; color: white; font-weight: bold; font-size: 0.9em; }
.required-text { color: #e53e3e; font-weight: bold; margin-left: 4px; }
.pass { background-color: #38a169; }
.fail { background-color: #e53e3e; }
.remove-logo-btn {
  background-color: #c53030;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.2s ease;
  white-space: nowrap;
}
.remove-logo-btn:hover { background-color: #9b2c2c; }
.tab-content { padding-top: 20px; }
.theme--dark.v-tabs-items { padding: 20px; }
.copyable-field-container {
  position: relative;
  margin-top: 15px;
}
.copy-button {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  background-color: #3f3f3f;
}
.code-textarea {
  --branding-code-font: 'Courier New', Courier, monospace;
  font-family: var(--branding-code-font);
}
.preview-launch-btn {
  min-width: 120px;
}
.preview-dialog-card {
  min-height: 100vh;
  background: #111827;
  color: #f8fafc;
}
.preview-dialog-toolbar {
  background: transparent !important;
  color: #f8fafc;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.preview-dialog-content {
  padding: 14px 18px 18px;
  background: transparent !important;
}
</style>
